import React from "react";
import { cn } from "@/lib/utils";
import { VariantProps, cva } from "class-variance-authority";
import { Loader2 } from "lucide-react";

const spinnerVariants = cva("flex-col items-center justify-center", {
  variants: {
    show: {
      true: "inline-flex",
      false: "hidden",
    },
  },
  defaultVariants: {
    show: true,
  },
});

// Sizes track the Tailwind `size-X` scale so callers don't need inline
// `h-X w-X` overrides. `sm`/`lg` are pixel-precise additions for sites that
// previously hard-coded `h-5 w-5` (20px) and `h-10 w-10` (40px); they sit
// between the named tiers (small=24, medium=32, large=48).
//
// No default text color — the Loader2 SVG strokes use `currentColor`, so the
// spinner inherits its parent's text color (e.g. `text-primary-foreground`
// inside a primary Button, `text-muted-foreground` inside a muted card).
// Callers that need an explicit color pass `className="text-X"`.
const loaderVariants = cva("animate-spin", {
  variants: {
    size: {
      xs: "size-4",
      sm: "size-5",
      small: "size-6",
      medium: "size-8",
      lg: "size-10",
      large: "size-12",
    },
  },
  defaultVariants: {
    size: "medium",
  },
});

interface SpinnerContentProps
  extends VariantProps<typeof spinnerVariants>,
    VariantProps<typeof loaderVariants> {
  className?: string;
  children?: React.ReactNode;
}

export function Spinner({
  size,
  show,
  children,
  className,
}: SpinnerContentProps) {
  return (
    <span className={spinnerVariants({ show })}>
      <Loader2 className={cn(loaderVariants({ size }), className)} />
      {children}
    </span>
  );
}
